import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../containers/ApiContainer';
import { useIdentity } from '../../../containers/IdentityContainer';
import { ACTIVATION_STATE, UserActivation, ActivationStateValue } from '../../../components/Activation/types';
import { ACTIVATION_STEPS, getCurrentStepIndex, getCompletedCount } from '../../../components/Activation';
import { SidebarChecklist } from '../../../components/Activation/variants/SidebarChecklist';
import { ActivationOverviewRow, ActivationAuditLog, ActivationUpdatePayload } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  user: ActivationOverviewRow;
  onUpdate: () => void;
}

/**
 * Get state label
 */
function getStateLabel(state: number, t: (key: string) => string): string {
  switch (state) {
    case ACTIVATION_STATE.DEEP:
      return t('screens.superAdmin.activation.overview.states.deep');
    case ACTIVATION_STATE.SHALLOW:
      return t('screens.superAdmin.activation.overview.states.shallow');
    default:
      return t('screens.superAdmin.activation.overview.states.pre');
  }
}

const CHECKLIST_STEPS = [
  { id: 'add_aircraft', field: 'step_add_aircraft_at' },
  { id: 'first_autostack', field: 'step_first_autostack_at' },
  { id: 'second_autostack', field: 'step_second_autostack_at' },
  { id: 'save_favorite', field: 'step_save_favorite_at' },
  { id: 'share_layout', field: 'step_share_layout_at' },
];

const STEP_LABELS: Record<string, string> = {
  add_aircraft: 'Add Aircraft',
  first_autostack: 'First AutoStack',
  second_autostack: 'Second AutoStack',
  save_favorite: 'Save Favorite',
  share_layout: 'Share Layout',
};

export const ActivationDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  user,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const { airplxIdentity } = useIdentity();

  // Form state - use defaults for users without activation records
  const [windowDays, setWindowDays] = React.useState(user.activation_window_days ?? 14);
  const [threshold, setThreshold] = React.useState(user.deep_activation_threshold ?? 3);
  const [notes, setNotes] = React.useState(user.admin_notes || '');
  const [reason, setReason] = React.useState('');
  const [startDate, setStartDate] = React.useState(
    user.activation_started_at
      ? new Date(user.activation_started_at).toISOString().slice(0, 16)
      : ''
  );

  // Detailed activation data
  const [fullData, setFullData] = React.useState<Record<string, unknown> | null>(null);
  const [auditLog, setAuditLog] = React.useState<ActivationAuditLog[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState(0);

  // Fetch full activation data and audit log
  React.useEffect(() => {
    if (!open || !postgrest) return;

    const fetchDetails = async () => {
      try {
        // Fetch full activation record
        const { data: activation } = await postgrest
          .from('user_activation')
          .select('*')
          .eq('user_id', user.user_id)
          .eq('fbo_id', user.fbo_id)
          .maybeSingle();

        setFullData(activation);

        // Fetch audit log
        const { data: logs } = await postgrest
          .from('activation_audit_log')
          .select('*')
          .eq('target_user_id', user.user_id)
          .eq('fbo_id', user.fbo_id)
          .order('created_at', { ascending: false })
          .limit(20);

        setAuditLog(logs || []);
      } catch (err) {
        console.error('Failed to fetch activation details:', err);
      }
    };

    fetchDetails();
  }, [open, postgrest, user.user_id, user.fbo_id]);

  // Handle save
  const handleSave = async () => {
    if (!postgrest || !airplxIdentity?.userid) return;

    setIsSaving(true);
    setError(null);

    try {
      const updates: ActivationUpdatePayload = {
        activation_window_days: windowDays,
        deep_activation_threshold: threshold,
        checklist_variant: 'sidebar',
        admin_notes: notes,
      };

      // Upsert activation record - creates if doesn't exist
      const { error: upsertError } = await postgrest
        .from('user_activation')
        .upsert({
          user_id: user.user_id,
          fbo_id: user.fbo_id,
          ...updates,
        }, { onConflict: 'user_id,fbo_id' });

      if (upsertError) throw upsertError;

      // Log the action
      await postgrest.from('activation_audit_log').insert({
        target_user_id: user.user_id,
        fbo_id: user.fbo_id,
        admin_user_id: airplxIdentity.userid,
        action: 'update_settings',
        old_value: {
          activation_window_days: user.activation_window_days,
          deep_activation_threshold: user.deep_activation_threshold,
          checklist_variant: user.checklist_variant,
          admin_notes: user.admin_notes,
        },
        new_value: updates,
        reason: reason || null,
      });

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to save activation settings:', err);
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle force to deep
  const handleForceDeep = async () => {
    if (!postgrest || !airplxIdentity?.userid) return;

    setIsSaving(true);
    setError(null);

    try {
      const { error: upsertError } = await postgrest
        .from('user_activation')
        .upsert({
          user_id: user.user_id,
          fbo_id: user.fbo_id,
          activation_state: ACTIVATION_STATE.DEEP,
          deep_activation_at: new Date().toISOString(),
        }, { onConflict: 'user_id,fbo_id' });

      if (upsertError) throw upsertError;

      // Log the action
      await postgrest.from('activation_audit_log').insert({
        target_user_id: user.user_id,
        fbo_id: user.fbo_id,
        admin_user_id: airplxIdentity.userid,
        action: 'force_deep',
        old_value: { activation_state: user.activation_state },
        new_value: { activation_state: ACTIVATION_STATE.DEEP },
        reason: reason || 'Admin forced deep activation',
      });

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to force deep activation:', err);
      setError('Failed to update activation state');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle updating start date
  const handleUpdateStartDate = async () => {
    if (!postgrest || !airplxIdentity?.userid || !startDate) return;

    setIsSaving(true);
    setError(null);

    try {
      await postgrest.rpc('admin_update_activation_start', {
        p_user_id: user.user_id,
        p_fbo_id: user.fbo_id,
        p_new_start_date: new Date(startDate).toISOString(),
        p_reason: reason || 'Admin adjusted activation start date',
      });

      onUpdate();
      // Refresh fullData
      const { data: activation } = await postgrest
        .from('user_activation')
        .select('*')
        .eq('user_id', user.user_id)
        .eq('fbo_id', user.fbo_id)
        .maybeSingle();
      setFullData(activation);
    } catch (err) {
      console.error('Failed to update start date:', err);
      setError('Failed to update start date');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle marking a step complete
  const handleMarkStepComplete = async (stepId: string) => {
    if (!postgrest || !airplxIdentity?.userid) return;

    setIsSaving(true);
    setError(null);

    try {
      await postgrest.rpc('admin_mark_step_complete', {
        p_user_id: user.user_id,
        p_fbo_id: user.fbo_id,
        p_step: stepId,
        p_reason: reason || `Admin marked ${stepId} as complete`,
      });

      onUpdate();
      // Refresh fullData and audit log
      const { data: activation } = await postgrest
        .from('user_activation')
        .select('*')
        .eq('user_id', user.user_id)
        .eq('fbo_id', user.fbo_id)
        .maybeSingle();
      setFullData(activation);

      const { data: logs } = await postgrest
        .from('activation_audit_log')
        .select('*')
        .eq('target_user_id', user.user_id)
        .eq('fbo_id', user.fbo_id)
        .order('created_at', { ascending: false })
        .limit(20);
      setAuditLog(logs || []);
    } catch (err) {
      console.error('Failed to mark step complete:', err);
      setError('Failed to mark step complete');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle unchecking a step (resetting to incomplete)
  const handleUncheckStep = async (stepId: string) => {
    if (!postgrest || !airplxIdentity?.userid) return;

    setIsSaving(true);
    setError(null);

    try {
      await postgrest.rpc('admin_uncheck_step', {
        p_user_id: user.user_id,
        p_fbo_id: user.fbo_id,
        p_step: stepId,
        p_reason: reason || `Admin unchecked ${stepId}`,
      });

      onUpdate();
      // Refresh fullData and audit log
      const { data: activation } = await postgrest
        .from('user_activation')
        .select('*')
        .eq('user_id', user.user_id)
        .eq('fbo_id', user.fbo_id)
        .maybeSingle();
      setFullData(activation);

      const { data: logs } = await postgrest
        .from('activation_audit_log')
        .select('*')
        .eq('target_user_id', user.user_id)
        .eq('fbo_id', user.fbo_id)
        .order('created_at', { ascending: false })
        .limit(20);
      setAuditLog(logs || []);
    } catch (err) {
      console.error('Failed to uncheck step:', err);
      setError('Failed to uncheck step');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {user.user_name || user.user_email}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {user.user_email} · {user.fbo_name}
        </Typography>
      </DialogTitle>
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Admin" />
        <Tab label="Preview" />
      </Tabs>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ py: 0.5, mb: 1.5 }}>{error}</Alert>}

        {activeTab === 0 && (
          <Stack spacing={1.5}>
            {/* Status row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={getStateLabel(user.activation_state, t)}
                color={
                  user.activation_state === ACTIVATION_STATE.DEEP
                    ? 'success'
                    : user.activation_state === ACTIVATION_STATE.SHALLOW
                    ? 'warning'
                    : 'default'
                }
              />
              <Typography variant="body2" color="text.secondary">
                {user.autostack_run_count}/{user.deep_activation_threshold} runs
              </Typography>
              {user.activation_started_at && (
                <Typography variant="body2" color="text.secondary">
                  · Expires {(() => {
                    const start = new Date(user.activation_started_at);
                    const exp = new Date(start);
                    exp.setDate(exp.getDate() + (user.activation_window_days || 14));
                    return exp.toLocaleDateString();
                  })()}
                </Typography>
              )}
            </Box>

            {/* Checklist Progress */}
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {t('screens.superAdmin.activation.detail.checklistProgress')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {CHECKLIST_STEPS.map((step) => {
                  const completed = fullData != null && fullData[step.field] != null;
                  const formatDateTime = (d: string) => {
                    const date = new Date(d);
                    const yyyy = date.getFullYear();
                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                    const dd = String(date.getDate()).padStart(2, '0');
                    const hh = String(date.getHours()).padStart(2, '0');
                    const min = String(date.getMinutes()).padStart(2, '0');
                    const tz = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                    return `${yyyy}-${mm}-${dd} ${hh}:${min} ${tz}`;
                  };
                  return (
                    <Tooltip
                      key={step.id}
                      title={completed ? t('screens.superAdmin.activation.detail.uncheckStep') : t('screens.superAdmin.activation.detail.markComplete')}
                      placement="left"
                    >
                      <Box
                        onClick={() => !isSaving && (completed ? handleUncheckStep(step.id) : handleMarkStepComplete(step.id))}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          py: 0.25,
                          px: 0.5,
                          borderRadius: 0.5,
                          cursor: isSaving ? 'wait' : 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {completed ? (
                          <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} color="disabled" />
                        )}
                        <Typography
                          variant="body2"
                          sx={{ flex: 1, fontSize: '0.8125rem', color: completed ? 'text.primary' : 'text.secondary' }}
                        >
                          {STEP_LABELS[step.id] || step.id}
                        </Typography>
                        {completed && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {formatDateTime(fullData[step.field] as string)}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Paper>

            {/* Settings */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ width: 100 }}>
                <InputLabel>Window</InputLabel>
                <Select value={windowDays} label="Window" onChange={(e) => setWindowDays(e.target.value as number)}>
                  <MenuItem value={7}>7d</MenuItem>
                  <MenuItem value={14}>14d</MenuItem>
                  <MenuItem value={30}>30d</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 100 }}>
                <InputLabel>Threshold</InputLabel>
                <Select value={threshold} label="Threshold" onChange={(e) => setThreshold(e.target.value as number)}>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Start Date - editable */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                type="datetime-local"
                label="Started"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ width: 200 }}
              />
              <Button size="small" variant="outlined" onClick={handleUpdateStartDate} disabled={isSaving || !startDate}>
                Set
              </Button>
              <TextField
                label="Reason"
                placeholder="For audit log"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
            </Stack>

            {/* History */}
            {auditLog.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  History
                </Typography>
                {auditLog.slice(0, 3).map((log) => {
                  const d = new Date(log.created_at);
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  const hh = String(d.getHours()).padStart(2, '0');
                  const min = String(d.getMinutes()).padStart(2, '0');
                  const tz = d.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                  return (
                    <Typography key={log.id} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      {log.action} — {yyyy}-{mm}-{dd} {hh}:{min} {tz}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </Stack>
        )}

        {activeTab === 1 && fullData && (
          <Box sx={{ position: 'relative', minHeight: 300, display: 'flex', justifyContent: 'center', pt: 2 }}>
            <Box sx={{
              position: 'relative',
              // Override fixed positioning from checklist components for preview
              '& > *': {
                position: 'static !important',
                bottom: 'auto !important',
                left: 'auto !important',
                right: 'auto !important',
              },
            }}>
              {(() => {
                // Build a mock activation object for the preview
                const mockActivation: UserActivation = {
                  user_id: user.user_id,
                  fbo_id: user.fbo_id,
                  activation_mode: true,
                  activation_state: user.activation_state as ActivationStateValue,
                  activation_started_at: user.activation_started_at,
                  activation_window_days: windowDays,
                  deep_activation_threshold: threshold,
                  autostack_run_count: user.autostack_run_count,
                  first_autostack_at: fullData.first_autostack_at as string | null,
                  deep_activation_at: fullData.deep_activation_at as string | null,
                  checklist_variant: 'sidebar',
                  step_add_aircraft_at: fullData.step_add_aircraft_at as string | null,
                  step_first_autostack_at: fullData.step_first_autostack_at as string | null,
                  step_second_autostack_at: fullData.step_second_autostack_at as string | null,
                  step_save_favorite_at: fullData.step_save_favorite_at as string | null,
                  step_share_layout_at: fullData.step_share_layout_at as string | null,
                  last_activity_at: fullData.last_activity_at as string | null,
                  admin_notes: fullData.admin_notes as string | null,
                  created_at: fullData.created_at as string,
                  updated_at: fullData.updated_at as string,
                };
                const currentStepIndex = getCurrentStepIndex(mockActivation);
                const completedCount = getCompletedCount(mockActivation);
                return (
                  <SidebarChecklist
                    activation={mockActivation}
                    steps={ACTIVATION_STEPS}
                    currentStepIndex={currentStepIndex}
                    completedCount={completedCount}
                    onStepClick={() => {}}
                  />
                );
              })()}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="warning"
          onClick={handleForceDeep}
          disabled={isSaving || user.activation_state === ACTIVATION_STATE.DEEP}
        >
          {t('screens.superAdmin.activation.detail.controls.forceDeep.label')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
